import { Box, Card, CardBody, Grid, GridItem, SimpleGrid, Text } from '@chakra-ui/react';
import React, { type ReactNode } from 'react';

import type { Portfolio } from '../_openapi/portfolio-optimizer';
import {
    createLinesInvestmentSetup,
    createLinesPortfolioFigures,
    createLinesPortfolioStatus,
} from './Tabs/PortfolioDetailsTab';

export type DataGridLine = {
    label: string;
    value: ReactNode;
};

const DataGrid = ({ title, lines }: { title: string; lines: DataGridLine[] }) => (
    <Box>
        <Text fontSize="lg" fontWeight="bold" mb={2}>
            {title}
        </Text>
        <SimpleGrid columns={2} borderWidth="1.2px" borderColor="whiteAlpha.40">
            {lines.map(({ label, value }) => (
                <React.Fragment key={label}>
                    <Text
                        pl={2}
                        pr={2}
                        borderWidth="1.2px"
                        borderColor="whiteAlpha.40"
                        wordBreak="break-word"
                        fontSize="sm"
                    >
                        {label}
                    </Text>
                    <Box
                        pl={2}
                        pr={2}
                        borderWidth="1.2px"
                        borderColor="whiteAlpha.40"
                        wordBreak="break-word"
                        fontSize="small"
                    >
                        {value}
                    </Box>
                </React.Fragment>
            ))}
        </SimpleGrid>
    </Box>
);

const PortfolioIdentificationGrid = ({ data }: { data: DataGridLine[] }) => (
    <Box>
        <Grid templateColumns="1fr 3fr 1fr 3fr" gap={0.5} borderWidth="2px" borderColor="whiteAlpha.40" p={2}>
            {data.map(({ label, value }, index) => (
                <React.Fragment key={label}>
                    <GridItem>
                        <Text fontSize="sm" fontWeight="medium">
                            {label}
                        </Text>
                    </GridItem>
                    <GridItem>
                        <Text fontSize="sm" pl={4} pr={4}>
                            {value}
                        </Text>
                    </GridItem>
                    {index % 2 === 1 && index < data.length - 1 && (
                        <GridItem colSpan={4} borderBottomWidth="2px" borderColor="whiteAlpha.50" my={1} />
                    )}
                </React.Fragment>
            ))}
        </Grid>
    </Box>
);

export const PortfolioInfoGrid = ({ portfolioDetails }: { portfolioDetails: Portfolio }) => (
    <Box>
        <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} width="100%">
            <Card>
                <CardBody>
                    <DataGrid title="Investment Setup" lines={createLinesInvestmentSetup(portfolioDetails)} />
                </CardBody>
            </Card>
            <Card>
                <CardBody>
                    <DataGrid title="Portfolio Figures" lines={createLinesPortfolioFigures(portfolioDetails)} />
                </CardBody>
            </Card>
            <Card>
                <CardBody>
                    <DataGrid title="Portfolio Status" lines={createLinesPortfolioStatus(portfolioDetails)} />
                </CardBody>
            </Card>
        </Grid>
    </Box>
);
